import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ContactPage() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: '', // Anti-spam
    timestamp_check: Date.now() // Anti-spam
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    address: 'Via Dante Alighieri 23, 90022 Caltavuturo (PA) – Italia',
    phone: '+39 379 1243094',
    phone_name: 'Carlo',
    email: 'info@caltavuturoclimbing.it',
    gps_lat: 37.8221,
    gps_lng: 13.8919
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/contact/settings`);
        if (res.data) {
          setSettings(res.data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t('requiredField'));
      return;
    }

    if (!formData.email.includes('@')) {
      toast.error(t('invalidEmail'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/message`, {
        ...formData,
        timestamp_check: formData.timestamp_check
      });
      setSuccess(true);
      toast.success(t('messageSent'));
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        honeypot: '',
        timestamp_check: Date.now()
      });
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-xl text-white mb-6">
              {t('contact')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Hai domande? Vuoi organizzare un\'uscita? Contattaci!'
                : 'Have questions? Want to organize a climbing trip? Contact us!'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding topo-bg">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="heading-md text-[#0A0A0A] mb-8">
                {language === 'it' ? 'Informazioni di contatto' : 'Contact Information'}
              </h2>
              
              <div className="space-y-6 mb-12">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-[#FF6D00]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#FF6D00]" />
                  </div>
                  <div>
                    <h3 className="font-heading uppercase mb-1">{t('address')}</h3>
                    <p className="text-gray-600">{settings.address}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-[#FF6D00]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#FF6D00]" />
                  </div>
                  <div>
                    <h3 className="font-heading uppercase mb-1">{t('phone')}</h3>
                    <p className="text-gray-600">{settings.phone_name}: {settings.phone}</p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 bg-[#FF6D00]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#FF6D00]" />
                  </div>
                  <div>
                    <h3 className="font-heading uppercase mb-1">{t('email')}</h3>
                    <a href={`mailto:${settings.email}`} className="text-[#FF6D00] hover:underline">
                      {settings.email}
                    </a>
                  </div>
                </motion.div>
              </div>

              {/* Map */}
              <div className="rounded-lg overflow-hidden h-80">
                <iframe
                  title="Location Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8'}&q=${settings.gps_lat},${settings.gps_lng}&zoom=14`}
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="heading-md text-[#0A0A0A] mb-8">
                {language === 'it' ? 'Inviaci un messaggio' : 'Send us a message'}
              </h2>

              {success ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16 bg-green-50 rounded-lg"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-heading text-xl uppercase mb-2">{t('success')}</h3>
                  <p className="text-gray-600 mb-6">{t('messageSent')}</p>
                  <Button onClick={() => setSuccess(false)} variant="outline">
                    {language === 'it' ? 'Invia un altro messaggio' : 'Send another message'}
                  </Button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Honeypot field - hidden from users */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('name')} *
                      </label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={language === 'it' ? 'Il tuo nome' : 'Your name'}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('email')} *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={language === 'it' ? 'La tua email' : 'Your email'}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('subject')}
                    </label>
                    <Input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder={language === 'it' ? 'Oggetto del messaggio' : 'Message subject'}
                      data-testid="contact-subject"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('message')} *
                    </label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={language === 'it' ? 'Scrivi il tuo messaggio...' : 'Write your message...'}
                      rows={6}
                      required
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={loading}
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {t('loading')}
                      </span>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t('send')}
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
